// Legacy variant of our JWT claims.
// Still needs to be supported for backwards compatibility.
interface ClaimsV1 {
  channelId?: string
  channelGroupId?: string
  excludedChannelIds?: string[]

  ingestPermission?: boolean
}

interface ClaimsV2 {
  version: string | number

  channelId?: string
  channelGroupId?: string
  excludedChannelIds?: string[]

  scope: {
    playout?: boolean
    ingest?: boolean
    clipping?: {
      from: string
      to: string
    }
  }
}

// Internal representation of our JWT claims.
export interface Claims {
  channelId?: string
  channelGroupId?: string
  excludedChannelIds?: string[]

  scope: Scope
}

export interface Scope {
  playout?: boolean
  ingest?: boolean
  clipping?: {
    from: Date
    to: Date
  }
}

export function toV2Claims(claims: Claims): ClaimsV2 {
  const clipping = claims.scope.clipping && {
    from: claims.scope.clipping.from.toISOString(),
    to: claims.scope.clipping.to.toISOString(),
  }

  return {
    version: 2,
    channelId: claims.channelId,
    channelGroupId: claims.channelGroupId,
    excludedChannelIds: claims.excludedChannelIds,
    scope: {
      playout: claims.scope.playout,
      ingest: claims.scope.ingest,
      clipping,
    },
  }
}

export function parseClaims(claims: unknown): Claims | undefined {
  if (isClaimsV1(claims)) {
    return parseV1Claims(claims)
  }

  return isClaimsV2(claims) ? parseV2Claims(claims) : undefined
}

function parseV1Claims(claims: ClaimsV1): Claims {
  return {
    channelId: claims.channelId,
    channelGroupId: claims.channelGroupId,
    excludedChannelIds: claims.excludedChannelIds,
    scope: {
      playout: true,
      ingest: claims.ingestPermission ?? false,
    },
  }
}

function parseV2Claims(claims: ClaimsV2): Claims {
  return {
    channelId: claims.channelId,
    channelGroupId: claims.channelGroupId,
    excludedChannelIds: claims.excludedChannelIds,
    scope: {
      playout: claims.scope.playout,
      ingest: claims.scope.ingest,
      clipping: claims.scope.clipping
        ? { from: new Date(claims.scope.clipping.from), to: new Date(claims.scope.clipping.to) }
        : undefined,
    },
  }
}

function isClaimsV2(claims: unknown): claims is ClaimsV2 {
  return (
    typeof claims === "object" &&
    claims !== null &&
    "version" in claims &&
    claims.version == 2 &&
    "scope" in claims &&
    claims.scope !== undefined
  )
}

function isClaimsV1(claims: unknown): claims is ClaimsV1 {
  if (typeof claims !== "object" || claims === null) {
    return false
  }

  if ("version" in claims && claims.version != 1) {
    return false
  }

  return true
}

export function scopeToString(scope: Scope): string {
  const playout = scope.playout ? "true" : "false"
  const ingest = scope.ingest ? "true" : "false"
  const clipping = scope.clipping
    ? `(from: ${scope.clipping.from.toISOString()}, to: ${scope.clipping.to.toISOString()})`
    : "false"
  return `playout: ${playout}, ingest: ${ingest}, clipping: ${clipping}`
}
